import { BaseComponentProps, WithClassname } from '../../types';
import React, { ReactElement } from 'react';
import classnames from 'classnames';
import useHelpers from '../../hooks/utility/useHelpers';
import useOngoingOrder from '../../hooks/context/useOngoingOrder';
import LoadingIndicator from '../loading/LoadingIndicator';
import { PaymentBreakdown } from '../../types/order';

type Props = WithClassname &
  BaseComponentProps & {
    data: PaymentBreakdown;
    animated?: boolean;
  };

export default function OrderPaymentBreakdown({ data, animated, className, children }: Props) {
  const { formatCurrency } = useHelpers();
  const { loadingDraft } = useOngoingOrder();

  const Row = ({
    name,
    amount,
    content,
    className,
  }: {
    name: any;
    amount?: number;
    content?: ReactElement;
    className?: string;
  }) => (
    <div className={classnames('flex justify-between text-sm gap-lg', className)}>
      <span className="flex items-center">{name}</span>
      <span>{content ?? formatCurrency(amount)}</span>
    </div>
  );

  return (
    <div className={className}>
      <Row name="Subtotal" amount={data.sub_total} />
      {data.tip > 0 && <Row name="Tip" amount={data.tip} className="mt-xs" />}
      {data.method_fee > 0 && (
        <Row name="Delivery fee" amount={data.method_fee} className="mt-xs" />
      )}
      {data.service_fee > 0 && (
        <Row name="Service fee" amount={data.service_fee} className="mt-xs" />
      )}
      {data.payment_method_upcharge_fee > 0 && (
        <Row
          name="Paypal fee"
          amount={data.payment_method_upcharge_fee}
          className="mt-xs relative"
        />
      )}
      {data.additional_tax > 0 && (
        <Row name="Taxes" amount={data.additional_tax} className="mt-xs" />
      )}
      {data.total_discount > 0 && (
        <Row name="Discount" amount={-data.total_discount} className="mt-xs" />
      )}
      <div className="flex justify-between text-sm gap-lg !text-base font-medium mt-xs">
        <span className="flex items-center">Total</span>
        <span>
          <span className={classnames({ hidden: animated && loadingDraft })}>
            {formatCurrency(data.total)}
          </span>
          <LoadingIndicator
            className={classnames('w-12', { hidden: !animated ? true : !loadingDraft })}
          />
        </span>
      </div>
      {children}
    </div>
  );
}
